import Vue from "vue";
import { BooleanHelper } from "~/helpers/booleanHelper";
import TrackedModel from "~/models/tracked/trackedModel";
import Model from "../model";
import ModelCollection from "../modelCollection";
import { RelationType } from "../relationType";


export default class UntrackedModel extends Model {
  _nameInParent: string;
  _isCollectionRoot: boolean;
  _deletions = [];

  asJson(optionsOverrides = {}): any {
    const options: {forLoad?: boolean, except: string[], include: string[]} = {except: ['except', 'rootModel', 'parent', 'store', 'expanded', 'author', 'pagination', 'createEmbeddedObject'], include: []};
    window.AppConfig.mergeJson(options, {except: this.except}, optionsOverrides);
    const json = {};
    const ownPropertyNames = Object.getOwnPropertyNames(this);
    const embeddedModelPropNames = Object.keys(this.EMBEDDED_MODELS);
    for (let i = 0; i < ownPropertyNames.length; i++) {
      const propertyName = ownPropertyNames[i];
      if (options.include.includes(propertyName) || !options.except || !options.except.includes(propertyName)) {
        if (!propertyName.startsWith('_')) {
          let attrPropName;
          if (options.forLoad) {
            attrPropName = propertyName;
          } else {
            attrPropName = `${propertyName}_attributes`;
          }
          if (Array.isArray(this[propertyName]) && this[propertyName][0] instanceof UntrackedModel) {
            json[attrPropName] = this[propertyName].map(model => model.asJson(optionsOverrides));
          } else if (this[propertyName] instanceof UntrackedModel || this[propertyName] instanceof TrackedModel) {
            json[attrPropName] = this[propertyName].asJson(optionsOverrides);
          } else if (this[propertyName] instanceof ModelCollection) {
            json[attrPropName] = this[propertyName].asJson(optionsOverrides);
          } else if (this.EMBEDDED_MODELS[propertyName] && this.EMBEDDED_MODELS[propertyName].relationType === RelationType.dictionary) {
            json[attrPropName] = json[attrPropName] || [];
            for (const key in this[propertyName]) {
              json[attrPropName].push(this[propertyName][key].asJson(options));
            }
          } else {
            if (!options.forLoad && !embeddedModelPropNames.includes(propertyName) && Array.isArray(this[propertyName]) && this[propertyName].length === 0) {
              json[propertyName] = ['']; // so rails knows to empty the array
            } else {
              json[propertyName] = this[propertyName];
            }
          }
        } else if (propertyName === '_destroy') {
          if (this[propertyName] === '1') {
            json[propertyName] = this[propertyName];
          }
        }
      }
    }

    // handle deletions of embedded documents
    if (options['includeDeletions']) {            
      this._deletions.forEach(deletion => {
        const collectionAttributesName = deletion.collectionName + '_attributes';
        json[collectionAttributesName].push({client_id: deletion.client_id, _destroy: true});
      });
    }

    return json;
  }

  trackDeletions(collectionName, childObject) {
    this._deletions.push({ collectionName: collectionName, client_id: childObject.client_id });
  }

  destroy() {
    super.destroy();
    
    if (this.parent) {
      const currentJson = this.asJson();
      delete currentJson.id;
      if (this.isCollectionRoot) {
        Vue.delete(this.parent[this.nameInParent], this.clientIdOrId);
      } else {
        Vue.delete(this.parent, this.nameInParent);
      }

      this.parent.trackDeletions(this.nameInParent, this);
      this.afterDestroy();      
    }
  }

  get isCollectionRoot(): boolean {
    if (BooleanHelper.isStrictBoolean(this._isCollectionRoot)) {
      return this._isCollectionRoot
    } else {
      return true
    }
  }
  
  // this really means something like 'propertyNameOnParent',
  // or nameInParent as below could be perfectly fine
  get rootName(): string {
    return null;
  }

  get nameInParent() {
    return this._nameInParent || this.rootName;
  }

  get hasParent(): boolean {
    return this.parent instanceof UntrackedModel;
  }
}